import { computed, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, map, merge } from 'rxjs';

/**
 * Estado de conectividade do navegador, exposto como signal. Atualiza via
 * eventos `online`/`offline` do `window` (listeners auto-removidos no destroy via
 * `takeUntilDestroyed`); `refresh()` reavalia sob demanda (ex.: "Reconectar").
 */
@Injectable({ providedIn: 'root' })
export class ConnectionStatusService {
  private readonly _isOnline = signal(this.readNavigatorOnline());

  public readonly isOnline = this._isOnline.asReadonly();

  public readonly isOffline = computed(() => !this._isOnline());

  constructor() {
    if (typeof window === 'undefined') return;

    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false)),
    )
      .pipe(takeUntilDestroyed())
      .subscribe((online) => this._isOnline.set(online));
  }

  /** Reavalia o estado atual e o retorna (`true` = online). */
  public refresh(): boolean {
    const online = this.readNavigatorOnline();
    this._isOnline.set(online);
    return online;
  }

  private readNavigatorOnline(): boolean {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }
}
